import strings from '../../strings'
import { Cue } from './webvtt'

export const formatPlayPauseLabel = (isPlaying: boolean, title: string) => {
	if (isPlaying) {
		return title
			? (strings.formatString(strings.accessibility_pause_audio_s, { title }) as string)
			: strings.pause_media
	} else {
		return title ? (strings.formatString(strings.accessibility_play_audio_s, { title }) as string) : strings.play_media
	}
}

/**
 * Index of the cue active at `currentTime`: the last cue whose start time has
 * been reached. Stays on the last-started cue through gaps and past the end so
 * the highlight doesn't flicker. Returns -1 before the first cue starts.
 *
 * Assumes cues are in ascending start order (as authored WebVTT is).
 */
export const findActiveCueIndex = (cues: Cue[], currentTime: number): number => {
	let active = -1
	for (let i = 0; i < cues.length; i++) {
		if (currentTime >= cues[i].start) active = i
		else break
	}
	return active
}

/**
 * Target scrollTop to keep the active cue centred, interpolated continuously
 * between the active cue's centre and the next cue's centre by how far playback
 * has progressed toward the next cue's start. Gives smooth glide instead of a
 * per-line jump. `centers[i]` is segment i's vertical centre within the scroll
 * content. Returns a non-negative scrollTop; the caller clamps the upper bound.
 */
export const getTranscriptScrollTarget = (
	centers: number[],
	cues: Cue[],
	currentTime: number,
	containerHeight: number,
): number => {
	const i = findActiveCueIndex(cues, currentTime)
	if (i < 0 || !centers.length) return 0
	const from = centers[Math.min(i, centers.length - 1)]
	const next = i + 1 < cues.length && i + 1 < centers.length ? centers[i + 1] : from
	const span = i + 1 < cues.length ? cues[i + 1].start - cues[i].start : 0
	const p = span > 0 ? Math.min(1, Math.max(0, (currentTime - cues[i].start) / span)) : 0
	const center = from + (next - from) * p
	return Math.max(0, center - containerHeight / 2)
}
